import React from "react";
import { Box, Typography } from "@mui/material";
import CInput from "../../../components/CInput";

const todayIso = () => new Date().toISOString().split("T")[0];

const HEADER_SX = {
  fontSize: 13,
  fontWeight: 500,
  color: "#949DAA",
  fontFamily: "Inter Tight",
};

const GRID = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "2fr 0.85fr 0.85fr" },
  columnGap: 3,
  rowGap: 1,
  alignItems: "center",
};

const dateInputSx = {
  width: "100%",
  "& fieldset": {
    padding: "2px 8px !important",
  },
  "& .MuiInputBase-input": {
    padding: "5px 6px !important",
    fontSize: 13,
  },
};

/**
 * `mode`:
 *  - "future" (default, used by RequestForApproval) — start can't be in
 *    the past, end ≥ start. No upper cap.
 *  - "past" (used by RequestForPayment) — start can't be in the future,
 *    end ≥ start AND ≤ cap. Cap = `maxDate` prop (typically the
 *    milestone's due date) or today if `maxDate` is missing.
 *
 * Either mode can be overridden directly by passing `minDate`/`maxDate`.
 */
export default function Deliverables({
  deliverables,
  onChange,
  mode = "future",
  minDate,
  maxDate,
}) {
  if (!Array.isArray(deliverables) || deliverables.length === 0) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography sx={{ fontSize: 13, color: "#9CA3AF" }}>
          No deliverables to schedule.
        </Typography>
      </Box>
    );
  }

  const updateDeliverable = (index, field, value) => {
    const next = deliverables.map((d, i) =>
      i === index ? { ...d, [field]: value } : d
    );
    onChange?.(next);
  };

  // Resolve absolute bounds. `mode="past"` caps the picker at the
  // milestone's due_date (passed in via `maxDate`) or today when no due
  // date exists, and removes the today-floor on `start_date` so the user
  // can backdate the activity to anything on or before that cap.
  const effectiveMaxDate =
    mode === "past" ? maxDate || todayIso() : maxDate || undefined;
  const effectiveMinDate =
    mode === "past" ? minDate || undefined : minDate || todayIso();

  return (
    <Box>
      <Box sx={{ ...GRID, mb: 1.5 }}>
        <Typography sx={HEADER_SX}>Deliverable Name</Typography>
        <Typography sx={{ ...HEADER_SX, display: { xs: "none", md: "block" } }}>
          Start Date
        </Typography>
        <Typography sx={{ ...HEADER_SX, display: { xs: "none", md: "block" } }}>
          End Date
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {deliverables.map((d, idx) => {
          // End picker is always ≥ start_date so the range stays valid.
          const endMin = d.start_date || effectiveMinDate;
          return (
            <Box key={d.id || idx} sx={{ ...GRID, py: 1 }}>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#0D0D0E",
                  fontFamily: "Inter Tight",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {d.deliverable_description ||
                  d.name ||
                  `Deliverable ${idx + 1}`}
              </Typography>
              <CInput
                type="date"
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  ...(effectiveMinDate ? { min: effectiveMinDate } : {}),
                  ...(effectiveMaxDate ? { max: effectiveMaxDate } : {}),
                }}
                sx={dateInputSx}
                value={d.start_date || ""}
                onChange={(e) => {
                  const v = e.target.value;
                  const next = { start_date: v };
                  // When the new start makes the existing end invalid,
                  // clear end_date so the user picks a fresh one — we no
                  // longer auto-snap end to match start.
                  if (d.end_date && v && d.end_date < v) {
                    next.end_date = "";
                  }
                  const updated = deliverables.map((x, i) =>
                    i === idx ? { ...x, ...next } : x
                  );
                  onChange?.(updated);
                }}
              />
              <CInput
                type="date"
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  ...(endMin ? { min: endMin } : {}),
                  ...(effectiveMaxDate ? { max: effectiveMaxDate } : {}),
                }}
                sx={dateInputSx}
                value={d.end_date || ""}
                onChange={(e) =>
                  updateDeliverable(idx, "end_date", e.target.value)
                }
                disabled={!d.start_date}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
